<template>
    <v-dialog
        :value="isOpen"
        max-width="500px"
        transition="slide-y-transition"
        eager
        @keydown.esc="onClose"
        @click:outside="onClose"
    >
        <v-card>
            <v-card-title>
                <h1 class="text-subtitle-1">
                    <template v-if="isNewScreenGroup">
                        Tworzenie nowej grupy
                    </template>
                    <template v-else>
                        Edycja grupy - <strong>{{ screenGroupData.name }}</strong>
                    </template>
                </h1>
            </v-card-title>

            <v-card-text class="text-center">
                <v-container fluid class="pa-0">
                    <v-row>
                        <v-col cols="12">
                            <v-text-field
                                v-model="screenGroup.name"
                                label="Nazwa grupy"
                                :error-messages="screenGroupNameErrors"
                                dense
                                outlined
                                required
                            />
                        </v-col>
                        <v-col v-if="!hideCustomerField" cols="12">
                            <v-autocomplete
                                v-model="screenGroup.customerId"
                                :items="customers"
                                :filter="customFilter"
                                :loading="customersSearchLoading"
                                :search-input.sync="customersSearch"
                                class="v-text-field--placeholder-dark"
                                :error-messages="customerIdErrors"
                                label="Klient"
                                required
                                dense
                                outlined
                                item-value="id"
                                no-data-text="Nie znaleziono"
                            >
                                <template slot="no-data">
                                    <v-list-item>
                                        <v-list-item-title>
                                            <template v-if="!customersSearch">
                                                Wpisz treść aby odnaleźć <strong>klienta</strong>.
                                            </template>
                                            <template v-else-if="customersSearchLoading">
                                                Trwa wyszukiwanie...
                                            </template>
                                            <template v-else>
                                                Nie znaleziono
                                            </template>
                                        </v-list-item-title>
                                    </v-list-item>
                                </template>
                                <template slot="selection" slot-scope="data">
                                    {{ data.item.name }}
                                </template>

                                <template slot="item" slot-scope="data">
                                    {{ data.item.name }}
                                </template>
                            </v-autocomplete>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>

            <v-divider />

            <v-card-actions>
                <v-btn
                    color="error"
                    text
                    data-sentry-label="Anuluj"
                    :disabled="actionLoading"
                    @click="onClose()"
                >
                    Anuluj
                </v-btn>
                <v-spacer />
                <v-btn
                    color="success"
                    text
                    data-sentry-label="Zapisz"
                    :loading="actionLoading"
                    @click="saveScreenGroup()"
                >
                    Zapisz
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { isEmpty } from 'lodash'
import { required } from 'vuelidate/lib/validators'
import { errorHandler, addSentryBreadcrumb } from '@/utils/errorHandler'
import customFilters from '@/mixins/customFilters'

let searchTimer

export default {
    name: 'OutdoorsLocalizationScreenGroupDialog',
    mixins: [ customFilters ],
    props: {
        isOpen: {
            type: Boolean
        },
        screenGroupData: {
            type: Object,
            default: null
        },
        outdoorsLocalizationScreenData: {
            type: Object,
            default: null
        },
        outdoorsLocalizationData: {
            type: Object,
            default: null
        },
        customerData: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            screenGroup: {
                name: null,
                customerId: null
            },
            actionLoading: false,
            outdoorsLocalizationScreen: {},
            customersSearch: null,
            customersSearchLoading: false,
            customers: [],
            hideCustomerField: false
        }
    },
    validations: {
        screenGroup: {
            name: {
                required
            },
            customerId: {
                required
            }
        }
    },
    computed: {
        isNewScreenGroup() {
            return isEmpty(this.screenGroupData)
        },
        screenGroupNameErrors() {
            const errors = []

            if (!this.$v.screenGroup.name.$dirty) return errors
            !this.$v.screenGroup.name.required && errors.push('Proszę podać nazwę grupy.')

            return errors
        },
        customerIdErrors() {
            const errors = []

            if (!this.$v.screenGroup.customerId.$dirty) return errors
            !this.$v.screenGroup.customerId.required && errors.push('Proszę podać klienta.')

            return errors
        }
    },
    watch: {
        isOpen(boolean) {
            if (boolean) {
                this.screenGroup = JSON.parse(JSON.stringify(this.screenGroupData))
                if (this.screenGroup.customer) {
                    this.customers.push(this.screenGroup.customer)
                    !isEmpty(this.customerData) ? this.hideCustomerField = true : this.hideCustomerField = false
                } else if (!isEmpty(this.customerData)) {
                    this.customers.push(this.customerData)
                    this.screenGroup.customerId = JSON.parse(JSON.stringify(this.customerData.id))
                }
            }
            this.outdoorsLocalizationScreen = JSON.parse(JSON.stringify(this.outdoorsLocalizationScreenData))
        },
        customersSearch(value = null) {
            if (value === null) return

            this.customersSearchLoading = true

            clearTimeout(searchTimer)

            searchTimer = setTimeout(() => {
                if (value) {
                    this.searchCustomers(value)
                } else {
                    this.customersSearchLoading = false
                    this.customers = []
                }
            }, 500)
        },
    },
    methods: {
        searchCustomers(query = '') {
            addSentryBreadcrumb('ui.action', 'Wyszukiwanie klienta')

            this.$store.dispatch('customers/getCustomers', {
                paginate: '?limit=20',
                query: `&hasOutdoorLocalizations=true&name=${query}`
            })
                .then(response => {
                    this.customers = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.customersSearchLoading = false
                })
        },
        saveScreenGroup() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            this.actionLoading = true

            let action = null

            this.isNewScreenGroup ? action = 'createLocalizationScreenGroup' : action = 'updateLocalizationScreenGroup'

            this.$store.dispatch(`outdoors/outdoorsSettings/${action}`, this.screenGroup)
                .then(response => {
                    this.outdoorsLocalizationScreen.localizationScreenGroup = response.data
                    this.outdoorsLocalizationScreen.localizationScreenGroupId = response.data.id
                    this.$emit('update-screen', this.outdoorsLocalizationScreen)
                    this.$emit('get-outdoors-localization-screen-groups')
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading = false
                    this.onClose()
                })
        },
        onClose() {
            this.actionLoading = false

            setTimeout(() => {
                this.$v.$reset()
                this.screenGroup.name = null
                this.screenGroup.customerId = null
                this.customers = []
                this.hideCustomerField = false
            }, 500)

            this.$emit('on-close')
        }
    }
}
</script>
